import type { WsMembershipAvailability } from "@storyboard/web-client";
import { store } from "@/store/store";
import type { MembershipAvailability } from "@/store/types";
import { isWorkspaceMembershipOffline, offlineThreshold, statusType } from "@/utils";

export function transformWsMembershipAvailability(record: WsMembershipAvailability): MembershipAvailability {
  // Preserve the tri-state: a membership with no volume report (e.g. never logged in) must stay
  // null, not collapse to false — false renders as "Device Muted" everywhere downstream.
  const mostRecentVolumeAudible = record.mostRecentVolume?.audible ?? null;
  const status = statusType(record.calculatedAvailability);
  const handsFreeUpdatedAt = record.mostRecentHandsFreeStatus?.updatedAt;
  const offline = isWorkspaceMembershipOffline({
    handsFreeUpdatedAt,
    offlineThreshold,
  });
  return {
    workspaceMembershipId: record.workspaceMembershipId,
    workspaceId: record.workspaceId,
    calculatedAvailability: record.calculatedAvailability,
    mostRecentAvailabilityChange: record.mostRecentAvailabilityChange ?? null,
    mostRecentHandsFreeStatusEnabled: record.mostRecentHandsFreeStatus?.enabled ?? null,
    mostRecentHandsFreeStatusChangedAt: record.mostRecentHandsFreeStatus?.changedAt ?? null,
    mostRecentHandsFreeStatusUpdatedAt: record.mostRecentHandsFreeStatus?.updatedAt ?? null,
    mostRecentVolumeVolume: record.mostRecentVolume?.volume ?? null,
    mostRecentVolumeAudible,
    mostRecentVolumeChangedAt: record.mostRecentVolume?.changedAt ?? null,
    mostRecentVolumeUpdatedAt: record.mostRecentVolume?.updatedAt ?? null,
    mostRecentDutyStatusStatus: record.mostRecentDutyStatus?.status ?? null,
    mostRecentDutyStatusSource: record.mostRecentDutyStatus?.source ?? null,
    mostRecentDutyStatusChangedAt: record.mostRecentDutyStatus?.changedAt ?? null,
    mostRecentDutyStatusUpdatedAt: record.mostRecentDutyStatus?.updatedAt ?? null,
    mostRecentDrivingStatus: record.mostRecentDrivingStatus?.status ?? null,
    mostRecentDrivingStatusChangedAt: record.mostRecentDrivingStatus?.changedAt ?? null,
    mostRecentDrivingStatusUpdatedAt: record.mostRecentDrivingStatus?.updatedAt ?? null,
    mostRecentDeviceLocationLatitude: record.mostRecentLocation?.latitude ?? null,
    mostRecentDeviceLocationLongitude: record.mostRecentLocation?.longitude ?? null,
    mostRecentDeviceLocationChangedAt: record.mostRecentLocation?.changedAt ?? null,
    mostRecentDeviceLocationUpdatedAt: record.mostRecentLocation?.updatedAt ?? null,
    mostRecentDeviceLocationNearestAddressMunicipality: record.mostRecentLocation?.nearestAddress?.municipality ?? null,
    mostRecentDeviceLocationNearestAddressRegion: record.mostRecentLocation?.nearestAddress?.region ?? null,
    mostRecentDeviceLocationNearestAddressCountry: record.mostRecentLocation?.nearestAddress?.country ?? null,
    mostRecentDeviceLocationSource: record.mostRecentLocation?.source ?? null,
    mostRecentDeviceLocationPlaceName: record.mostRecentLocation?.placeName ?? null,
    updatedAt: record.updatedAt ?? null,
    status,
    offline,
  };
}

/**
 * Availability heartbeats fan out to thousands of rows per AppSync drain on large fleets. Store
 * writes are O(delta) and notify per row, so the grid cells that subscribe by membership id
 * re-render individually — the old emit coalescing and whole-grid requery are gone (WEB-2202).
 */
export async function bulkUpsertMembershipAvailability(records: MembershipAvailability[]) {
  if (records.length === 0) return;
  store.upsert("wsMembershipAvailability", records);
}

export async function bulkUpsertWsMembershipAvailability(records: WsMembershipAvailability[]) {
  await bulkUpsertMembershipAvailability(records.map(transformWsMembershipAvailability));
}
